<script setup lang="ts">
import type { ScopeNode } from '@/types/bbel-types'
import { sampleFromHint } from '@/views/builder/components/bbel/bbel-palette'

/**
 * ADR-034 binary source picker tree — shares the scope-tree *look* with the BBEL palette
 * (scope-tree.css) but owns its own *behaviour*: the whole row is a single click target with a
 * consistent hover, a caret (not the label) toggles expansion, and the chosen node is highlighted.
 * The top level is the service grouping (depth 0) — not itself a valid binding, so its row expands;
 * deeper nodes (a projection or a member within it) select. No insert `+` — this authors a value,
 * it never pastes into an editor.
 */
const props = withDefaults(
  defineProps<{
    nodes: ScopeNode[]
    expanded: Set<string>
    selectedPath?: string
    depth?: number
  }>(),
  { depth: 0 },
)

const emit = defineEmits<{
  select: [path: string]
  toggle: [path: string]
}>()

// Service groups (depth 0) aren't bindable — their row expands; every deeper row selects.
function onRow(node: ScopeNode) {
  if (props.depth === 0 && node.children?.length) emit('toggle', node.path)
  else if (props.depth > 0) emit('select', node.path)
  else emit('toggle', node.path)
}

// Leaves show their sampled value inline (muted), matching the palette.
function sample(node: ScopeNode): string {
  return node.children?.length ? '' : sampleFromHint(node.hint)
}
</script>

<template>
  <ul class="bbel-tree list-unstyled mb-0">
    <li v-for="node in nodes" :key="node.path">
      <div
        class="bbel-tree-row bbel-tree-pick d-flex align-items-center"
        :class="{ 'bbel-tree-selected': depth > 0 && node.path === selectedPath }"
        @click="onRow(node)"
      >
        <button
          v-if="node.children?.length"
          type="button"
          class="bbel-tree-caret btn btn-sm p-0"
          @click.stop="emit('toggle', node.path)"
        >
          <i
            class="bi small"
            :class="expanded.has(node.path) ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
        </button>
        <span v-else class="bbel-tree-caret"></span>
        <span class="bbel-tree-label flex-grow-1">{{ node.label }}</span>
        <span v-if="sample(node)" class="bbel-tree-sample text-truncate" :title="node.hint">
          {{ sample(node) }}
        </span>
      </div>
      <div v-if="node.children?.length && expanded.has(node.path)" class="bbel-tree-children">
        <BinarySourceTree
          :nodes="node.children"
          :expanded="expanded"
          :selected-path="selectedPath"
          :depth="depth + 1"
          @select="emit('select', $event)"
          @toggle="emit('toggle', $event)"
        />
      </div>
    </li>
  </ul>
</template>

<style scoped src="../bbel/scope-tree.css"></style>
<style scoped>
/* Picker behaviour: every row is one click target with a pointer; the selected node persists a
   subtle highlight (not just hover). */
.bbel-tree-pick {
  cursor: pointer;
}
.bbel-tree-selected,
.bbel-tree-selected:hover {
  background: var(--bs-primary-bg-subtle);
}
</style>
